import { Component, OnDestroy, OnInit, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import validator from 'validator';
import { SynchronizationService } from '../synchronization.service';
import { Synchronization } from '../synchronization.model';
import { MainStore } from 'src/app/common/main.store';
import { CommonService } from 'src/app/services/common.service';
import { environment } from 'src/environments/environment';
import { Department } from '../../customer/customer.model';
import { DepartmentService } from 'src/app/services/department.service';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule } from '@angular/forms';
import { CountdownTimerComponent } from 'src/app/common/countdown-timer/countdown-timer.component';
import { QueueRequestBrowserComponent } from '../queue-request-browser/queue-request-browser.component';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorService } from '../../../common/errors/error.service';

@Component({
  selector: 'app-synchronization-detail',
  templateUrl: './synchronization-detail.component.html',
  styleUrl: './synchronization-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgbTooltip, FormsModule, CountdownTimerComponent, QueueRequestBrowserComponent]
})
export class SynchronizationDetailComponent implements OnInit, OnDestroy {

  protected isAdminUser: boolean = false;
  protected isLoading: boolean = true;
  protected errorMessage: string | null = null;
  protected synchronization: Synchronization = {} as Synchronization;
  protected selectedDepartment: Department;


  // UI helpers
  protected externalkey: string = '';
  protected showExternalKeyInClearText: boolean = false;
  private wasActivated: boolean = false;
  protected showActivateSync: boolean = false;
  protected showDetailsExpanded: boolean = false;

  // ViewChild for countdown timer
  private countdownTimer = viewChild<CountdownTimerComponent>('countdownTimer');

  // Syncronization Timers.
  private checkKeyTime = 15 * 1000;  // 15 sec  
  protected refreshKeyTime = 5 * 60000; // 5 min
  private checkActivatedInterval: any;
  private refreshKeyInterval: any;

  constructor(
    private store: MainStore,
    private route: ActivatedRoute,
    private router: Router,
    private toastrService: ToastrService,
    private errorService: ErrorService,
    private synchronizationService: SynchronizationService,
    private commonService: CommonService,
    departmentService: DepartmentService,

  ) {
    this.isAdminUser = this.store.currentUser.get().isAdmin && this.store.currentUser.get().departmentCode == '200'
    this.showActivateSync = environment.adminConfig.syncApiUrl != '';
    this.selectedDepartment = departmentService.getDepartment();
  }


  ngOnDestroy(): void {
    if (this.checkActivatedInterval != null) {
      clearInterval(this.checkActivatedInterval);
    }
    if (this.refreshKeyInterval != null) {
      clearInterval(this.refreshKeyInterval);
    }
  }

  async ngOnInit(): Promise<void> {
    const _syncId = this.route.snapshot.paramMap.get('synccontractid');
    if (_syncId == null || !validator.isUUID(_syncId)) {
      this.errorService.notify('SynkroniseringsId [' + this.route.snapshot.paramMap.get('synccontractid') + '] er ikke gyldigt!');
      this.router.navigate(['/home']);
      return;
    }

    try {
      const synchronization = await this.synchronizationService.getSynchronization(_syncId);
      if (synchronization == null) {
        // HTTP-fejl vises globalt af GlobalErrorInterceptor/ErrorService
      }
      else {
        this.synchronization = synchronization;
        if (this.synchronization.activated == null) {
          this.showDetailsExpanded = true;
          this.refreshActivationKey();

          this.checkActivatedInterval = setInterval(async () => {
            const activated = await this.synchronizationService.isActivated(this.synchronization.id);
            if (activated) {
              clearInterval(this.checkActivatedInterval);
              clearInterval(this.refreshKeyInterval);
              this.countdownTimer()?.stop();

              await this.synchronizationService.getSynchronization(_syncId).then((sync: Synchronization) => {
                this.synchronization = sync;
                this.externalkey = sync.externalKey;
                this.wasActivated = this.synchronization.enabled;
              })
                .catch(() => {
                });
            }
          }, this.checkKeyTime);
        }
        else {
          this.externalkey = this.synchronization.externalKey;
          this.wasActivated = this.synchronization.enabled;
        }
      }
    } catch (error) {
      const httpError = error as HttpErrorResponse;
      if (httpError?.status === 403) {
        this.errorMessage = httpError.error?.message ?? 'Du har ikke adgang til denne synkronisering.';
      } else {
        // HTTP-fejl vises globalt af GlobalErrorInterceptor/ErrorService
      }
    } finally {
      this.isLoading = false;

    }
  }

  protected navigateToList(): void {
    this.router.navigate(['synchronizations']);
  }

  protected reloadPage(): void {
    window.location.reload();
  }

  protected copyExternalKey() {
    navigator.clipboard.writeText(this.externalkey).then(() => {
      this.toastrService.success('Aktiveringsnøgle er kopieret til udklipsholder');
    }).catch(() => {
      this.errorService.warning('Kunne ikke kopiere aktiveringsnøgle til udklipsholder');
    });
  }

  protected copyApiKey() {
    navigator.clipboard.writeText(this.synchronization.key).then(() => {
      this.toastrService.success('Nøgle er kopieret til udklipsholder');
    }).catch(() => {
      this.errorService.warning('Kunne ikke kopiere nøgle til udklipsholder');
    });
  }

  protected toggleKey() {
    this.showExternalKeyInClearText = !this.showExternalKeyInClearText
    this.externalkey = this.showExternalKeyInClearText ? this.synchronization.externalKeyClearText : this.synchronization.externalKey;
  }

  private setRefreshKeyInterval() {
    if (this.refreshKeyInterval != null) {
      clearInterval(this.refreshKeyInterval);
    }

    this.refreshKeyInterval = setInterval(async () => {
      const syncKey = await this.synchronizationService.refreshActivationKey(this.synchronization.id);
      if (syncKey != null && syncKey.activationkey != '') {
        this.synchronization.key = syncKey.activationkey;
        // Restart countdown
        this.countdownTimer()?.start();
      }
    }, this.refreshKeyTime);
  }

  protected async refreshActivationKey() {
    const syncKey = await this.synchronizationService.refreshActivationKey(this.synchronization.id);
    if (syncKey == null || syncKey == '') {
      // HTTP-fejl vises globalt af GlobalErrorInterceptor/ErrorService
      return;
    }

    this.synchronization.key = syncKey.activationkey;

    // Start countdown timer
    this.countdownTimer()?.start();

    this.setRefreshKeyInterval();
  }

  protected onDisable() {
    if (this.synchronization.enabled && this.wasActivated) {
      this.commonService.confirm('Deaktiver synkronisering', 'Der vil ikke kunne foretages synkroniseringer via denne konrakt! Vil du deaktivere synkroniserigen?', 'Ja', 'Nej', 'lg')
        .then((confirmed) => {
          if (confirmed) {
            this.synchronization.enabled = false;
          }
          else { this.synchronization.enabled = true; }
        })
        .catch(() => { this.synchronization.enabled = true; });
    }
  }

  protected onUpdateSyncronization() {
    // Ensure departmentId is set     
    this.synchronization.departmentId = this.selectedDepartment.id;
    this.synchronization.departmentCode = this.selectedDepartment.code;

    this.synchronizationService.updateSynchronization(this.synchronization).then(x => {
      this.toastrService.success('Synkroniseringskontrakten er opdateret');
      this.wasActivated = this.synchronization.enabled;
    })
      .catch(() => {
        // HTTP-fejl vises globalt af GlobalErrorInterceptor/ErrorService
      })
      .finally(() => {
        this.synchronization.changed = '';
      });
  }

  protected async activateSync() {
    const action = await this.synchronizationService.showActivateSync(this.synchronization.key);
    if (action) {
      const activated = await this.synchronizationService.isActivated(this.synchronization.id);
      if (activated) {
        clearInterval(this.checkActivatedInterval);
        clearInterval(this.refreshKeyInterval);

        await this.synchronizationService.getSynchronization(this.synchronization.id).then((sync: Synchronization) => {
          this.synchronization = sync;
          this.externalkey = sync.externalKey;
          this.wasActivated = this.synchronization.enabled;
        })
          .catch(() => {
          });
      }

    }
  }

  protected deleteSync() {
    this.commonService.confirm('Slet synkronisering', 'Vil du slette synkroniseringskontrakten?', 'Ja', 'Nej', 'lg')
      .then((confirmed) => {
        if (confirmed) {
          this.synchronizationService.deleteSynchronization(this.synchronization.id).then(x => {
            this.toastrService.success('Synkroniseringskontrakten er slettet');
            this.router.navigate(['synchronizations']);
          })
            .catch(() => {
              // HTTP-fejl vises globalt af GlobalErrorInterceptor/ErrorService
            });
        }
      })
      .catch(() => { });
  }

  protected downloadSyncContract(event: any): void {
    event.preventDefault();

    const jsonData = {
      environment: environment.adminConfig.syncApiUrl,
      apikey: this.synchronization.key,
      externalKey: this.synchronization.externalKey
    };

    const url = window.URL.createObjectURL(new Blob([JSON.stringify(jsonData, null, 3)], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'crono.sync.auth.json';
    a.click();
    window.URL.revokeObjectURL(url);
  }
  //#endregion
}
